import { ErrorBanner, SkeletonText } from '@/components';
import { ShipmentPushStatus, ShipmentStatus } from '@/constants/shipment';
import { addMinuteQuantity, camelToRegularText, convertCurrency, handleOpenChat } from '@/helpers';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import { useGetShipmentDetailQuery, useGetUnfulfilledShipmentDetailQuery, useResyncShipmentDetailMutation } from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import shipmentSlice, { courierModalSelector, disabledBtnSelector } from '@/redux/features/shipment.slice';
import { ITrackingDetail } from '@/types/shipment';
import { Banner, BlockStack, Button, Card, Divider, Icon, InlineStack, SkeletonBodyText, Text, Tooltip } from '@shopify/polaris';
import {
  AlertTriangleIcon,
  CheckIcon,
  DeliveryIcon,
  InfoIcon,
  PackageFulfilledIcon,
  PageClockIcon,
  QuestionCircleIcon,
  RefreshIcon,
} from '@shopify/polaris-icons';
import dayjs from 'dayjs';
import { Fragment, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import CourierModal from '../../../ShipmentTable/components/CourierModal';
import { EShipmentType } from '@/pages/Shipments/config';
import ProductCard from '../ProductCard';
import ShipmentStepper from '../ShipmentStepper';
import { ShipmentStatusStyled, ShipmentUnfulfilledBannerStyled, ShipmentUntrackedBannerStyled, StyledShipmentInfo } from './styled';

const ShipmentInfo = () => {
  const location = useLocation();
  // Lấy state được truyền qua navigate
  const filter = location?.state?.filter;
  const { id } = useParams();
  const dispatch = useDispatch();
  const disabledBtn = useSelector(disabledBtnSelector);
  const isExistedId = disabledBtn.detail?.length > 0 && disabledBtn.detail?.some((item) => item.id === id || '');

  // Check if shipment is from unfulfilled tab
  // First try to get from location state, fallback to URL query params for page reload persistence
  const filterTypeFromState = filter
    ? (typeof filter === 'string' ? JSON.parse(filter) : filter)?.type
    : undefined;
  const searchParams = new URLSearchParams(location.search);
  const filterTypeFromUrl = searchParams.get('type') || undefined;
  const filterType = filterTypeFromState || filterTypeFromUrl;
  const isUnfulfilledShipment = filterType === EShipmentType.UNFULFILLED_SHIPMENT;

  // Construct filter object for API call
  // Use location state filter if available, otherwise construct minimal filter from URL params
  const apiFilter = filter || (filterType ? { type: filterType } : undefined);
  const shipmentQuery = useGetShipmentDetailQuery(
    {
      id: Number(id),
      filter: apiFilter ? JSON.stringify(apiFilter) : undefined,
    },
    { skip: !id || isUnfulfilledShipment },
  );
  const unfulfilledShipmentQuery = useGetUnfulfilledShipmentDetailQuery(
    {
      id: Number(id),
      filter: apiFilter ? JSON.stringify(apiFilter) : undefined,
    },
    { skip: !id || !isUnfulfilledShipment },
  );
  const { data, isLoading } = isUnfulfilledShipment ? unfulfilledShipmentQuery : shipmentQuery;
  const [resyncShipment, resyncShipmentData] = useResyncShipmentDetailMutation();
  const shipment = isUnfulfilledShipment
    ? (data as any)?.data?.shipmentUnfulfill
    : (data as any)?.data?.shipment;
  const [apiError, setApiError] = useState<string>('');
  const courierModal = useSelector(courierModalSelector);

  const isDisabledBtn = useMemo(() => {
    if (isExistedId) {
      const detailInfo = disabledBtn.detail.find((item) => item.id === String(id || ''));
      return new Date() < new Date(detailInfo?.time || '') && String(detailInfo?.id || '') === String(id || '');
    }
    return false;
  }, [disabledBtn.detail, id, isExistedId]);

  const renderShipmentDescription = (trackingDetail: ITrackingDetail[]) => {
    if (trackingDetail.length > 0) {
      const trackingPageData = trackingDetail.map(({ date_time, message }) => {
        return {
          time: date_time,
          title: message,
        };
      });
      return <ShipmentStepper data={trackingPageData} />;
    }
    return null;
  };

  const handleResync = async () => {
    resyncShipment({
      trackingCompany: shipment?.trackingCompany || '',
      trackingNumber: shipment?.trackingNumber || '',
      orderId: shipment?.orderId || '',
    }).then((data) => {
      let updatedDetail = disabledBtn.detail;
      if ('data' in data && data.data && data.data?.state === 1) {
        updatedDetail = isExistedId
          ? disabledBtn.detail.map((item) => {
              if (item.id === String(id || '')) {
                return {
                  ...item,
                  time: addMinuteQuantity(5),
                };
              }
              return item;
            })
          : [
              ...disabledBtn.detail,
              {
                id: id || '',
                time: addMinuteQuantity(5),
              },
            ];
        dispatch(
          shipmentSlice.actions.handleSetDisabledBtnTime({
            ...disabledBtn,
            detail: updatedDetail,
            detailTooltip:
              'Shipment details are re-synced successfully! The latest info is now updated on tracking page as well. You can continue re-syncing for the next 5 minute',
          }),
        );
        dispatch(commonSlice.actions.handleToast(handleToastMutation(data)));
        if (apiError) {
          setApiError('');
        }
        return;
      }
      setApiError(getErrorFromAPI(data));
    });
  };

  const handleClickEditCourierBtn = (id: number, courier: string) =>
    dispatch(shipmentSlice.actions.handleChangeCourierModal({ ...courierModal, open: true, value: courier, id: [id] }));

  const getStatusStyle = (status?: ShipmentStatus) => {
    switch (status) {
      case ShipmentStatus.CourierNotDetected: {
        return {
          icon: PageClockIcon,
          background: '#E3E3E3',
          color: '#4A4A4A',
        };
      }
      case ShipmentStatus.NotFound:
        return {
          icon: PageClockIcon,
          background: '#E3E3E3',
          color: '#4A4A4A',
          banner: {
            title: 'The carrier hasn’t updated tracking details yet.',
            description: 'We’ll display them as soon as they’re available.',
            action: {
              content: 'Change carrier',
              url: '',
              onAction: () => {
                shipment && handleClickEditCourierBtn(Number(shipment.id), shipment.trackingCompany || '');
              },
            },
          },
        };
      case ShipmentStatus.InformationReceived:
        return { icon: PackageFulfilledIcon, background: '#EAF4FF', color: '#0094D5' };
      case ShipmentStatus.Pending:
        return {
          icon: PageClockIcon,
          background: '#E3E3E3',
          color: '#4A4A4A',
          banner: {
            title: 'Tracking information may take some time to appear.',
            description: 'Please check back later in a few hours.',
            // Pending shipment already pushed to central (COMPLETED) but no carrier data yet —
            // let merchant switch carrier to recover it.
            ...(shipment?.pushStatus === ShipmentPushStatus.COMPLETED && {
              action: {
                content: 'Change carrier',
                url: '',
                onAction: () => {
                  shipment && handleClickEditCourierBtn(Number(shipment.id), shipment.trackingCompany || '');
                },
              },
            }),
          },
        };
      case ShipmentStatus.InTransit:
        return { icon: DeliveryIcon, background: '#EAF4FF', color: '#0094D5' };
      case ShipmentStatus.OutForDelivery:
        return { icon: DeliveryIcon, background: '#EAF4FF', color: '#0094D5' };
      case ShipmentStatus.AvailableForPickup:
        return { icon: CheckIcon, background: '#CDFEE1', color: '#29845A' };
      case ShipmentStatus.Delivered:
        return { icon: CheckIcon, background: '#CDFEE1', color: '#29845A' };
      case ShipmentStatus.FailedAttempt:
        return { icon: DeliveryIcon, background: '#FFF8DB', color: '#998A00' };
      case ShipmentStatus.Exception:
        return {
          icon: AlertTriangleIcon,
          background: '#FFF8DB',
          color: '#998A00',
        };
      case ShipmentStatus.CourierNotSupported:
        return {
          icon: QuestionCircleIcon,
          background: '#FFF1E3',
          color: '#B28400',
          banner: {
            title: 'We couldn’t detect the courier.',
            description:
              'Please double-check the carrier name (it may have different variations) or select the correct carrier from the list.',
            action: {
              content: 'Change carrier',
              url: '',
              onAction: () => {
                shipment && handleClickEditCourierBtn(Number(shipment.id), shipment.trackingCompany || '');
              },
            },
            secondaryAction: { content: 'Contact support', url: '', onAction: handleOpenChat },
          },
        };
      case ShipmentStatus.Expired:
        return {
          icon: PageClockIcon,
          background: '#FFF1E3',
          color: '#B28400',
          banner: {
            title: 'No new tracking information received.',
            description:
              'The carrier hasn’t provided updates since the last status. We recommend contacting the carrier directly for more details.',
          },
        };
      case ShipmentStatus.OutOfRange:
        return {
          icon: PageClockIcon,
          background: '#FFF1E3',
          color: '#B28400',
          banner: {
            title: 'This shipment is outside your current quota period.',
            description:
              "Orders created before the start of your current quota period aren't tracked. Tracking updates for this shipment won't be refreshed.",
          },
        };
      default:
        return { icon: PageClockIcon, background: '#E3E3E3', color: '#4A4A4A' };
    }
  };

  const { icon, background, color, banner } = getStatusStyle(
    shipment?.pushStatus === ShipmentPushStatus.NOT_DETECT
      ? ShipmentStatus.CourierNotSupported
      : shipment?.pushStatus === ShipmentPushStatus.OUT_RANGE_QUOTA
      ? ShipmentStatus.OutOfRange
      : (shipment?.trackingSummary as ShipmentStatus),
  );

  // Show amounts in the currency the buyer was actually charged (presentment) when available,
  // falling back to the shop's base currency for older shipments without presentment data.
  const usePresentment = !!(shipment?.lineItems?.[0] as any)?.priceSet?.presentment_money?.amount;
  const displayCurrency =
    (usePresentment
      ? (shipment?.lineItems?.[0] as any)?.priceSet?.presentment_money?.currency_code
      : shipment?.currency) ||
    shipment?.currency ||
    'USD';

  const resolveItemPrice = (item: any) => Number(usePresentment ? item.priceSet?.presentment_money?.amount : item.price);
  const resolveItemDiscount = (item: any) =>
    (item.discountAllocations || []).reduce(
      (acc: number, curr: any) =>
        acc + Number(usePresentment ? curr.amount_set?.presentment_money?.amount ?? curr.amount : curr.amount),
      0,
    );

  const totalPrice = useMemo(() => {
    return (shipment?.lineItems || []).reduce((acc: number, item: any) => {
      const finalPrice = resolveItemPrice(item) - resolveItemDiscount(item) / item.currentQuantity;
      return acc + finalPrice * item.quantity;
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment?.lineItems, usePresentment]);

  return (
    <>
      <StyledShipmentInfo>
        <Card>
          {isLoading ? (
            <SkeletonBodyText lines={6} />
          ) : isUnfulfilledShipment ? (
            <ShipmentUnfulfilledBannerStyled>
              <div className="icon">
                <Icon source={InfoIcon} />
              </div>
              <Text variant="bodyMd" as="p" fontWeight="bold">
                This shipment has not been fulfilled.
              </Text>
            </ShipmentUnfulfilledBannerStyled>
          ) : shipment?.trackingNumber ? (
            <BlockStack gap="400">
              <div className="shipment-header">
                <BlockStack gap="400">
                  <ErrorBanner isVisible={!!apiError}>{apiError}</ErrorBanner>
                  <div className="shipment-title">
                    <ShipmentStatusStyled $background={background} $color={color}>
                      <div className="icon">
                        <Icon source={icon} />
                      </div>
                      <Text variant="headingLg" as="p">
                        {shipment?.trackingSummary === ShipmentStatus.NotFound
                          ? 'No info'
                          : shipment?.pushStatus === ShipmentPushStatus.NOT_DETECT
                          ? 'Courier not detected'
                          : shipment?.pushStatus === ShipmentPushStatus.OUT_RANGE_QUOTA
                          ? 'Out of range'
                          : camelToRegularText(shipment?.trackingSummary || '')}
                      </Text>
                    </ShipmentStatusStyled>
                    {shipment?.pushStatus !== ShipmentPushStatus.NOT_DETECT && (
                      <Tooltip
                        zIndexOverride={isDisabledBtn || shipment?.pushStatus === ShipmentPushStatus.PROCESSING ? 400 : -1000}
                        content={
                          shipment?.pushStatus === ShipmentPushStatus.PROCESSING
                            ? 'This shipment is being processed. Re-sync is not available.'
                            : disabledBtn.detailTooltip
                        }
                      >
                        <Button
                          icon={RefreshIcon}
                          onClick={handleResync}
                          loading={resyncShipmentData.isLoading}
                          disabled={
                            isDisabledBtn ||
                            !shipment?.trackingNumber ||
                            shipment?.pushStatus === ShipmentPushStatus.PROCESSING
                          }
                        >
                          Re-sync
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </BlockStack>
              </div>
              {'webhookReceivedAt' in shipment && shipment.webhookReceivedAt && (
                <InlineStack align="end">
                  <Text variant="bodyMd" as="p" tone="subdued">
                    Last updated at: {dayjs(shipment.webhookReceivedAt).format('YYYY-MM-DD HH:mm:ss')}
                  </Text>
                </InlineStack>
              )}
              {banner && (
                <Banner title={banner.title} action={banner.action} secondaryAction={banner.secondaryAction}>
                  <p>{banner.description}</p>
                </Banner>
              )}
              {shipment?.trackingDetail?.length ? (
                <div className="shipment-description">{renderShipmentDescription(shipment.trackingDetail)}</div>
              ) : null}
            </BlockStack>
          ) : (
            <ShipmentUntrackedBannerStyled>
              <div className="icon">
                <Icon source={InfoIcon} />
              </div>
              <Text variant="bodyMd" as="p" fontWeight="bold">
                This shipment does not require delivery tracking.
              </Text>
            </ShipmentUntrackedBannerStyled>
          )}
        </Card>
        <Card>
          <div className="shipment-items">
            <Text variant="headingMd" as="h6">
              {isLoading ? <SkeletonText height="1.5rem" width="10rem" /> : `Shipment items`}
            </Text>
            <div className="product-list">
              {isLoading ? (
                <div className="product-loading">
                  <SkeletonBodyText lines={5} />
                </div>
              ) : (
                (shipment?.lineItems || []).map(({ productId, image, title, ...item }: any) => {
                  const price = resolveItemPrice(item);
                  const finalPrice = price - resolveItemDiscount(item) / item.currentQuantity;
                  const hasDiscount = price > finalPrice;
                  return (
                    <Fragment key={productId}>
                      <ProductCard
                        image={image || ''}
                        title={title}
                        quantity={item.quantity.toString()}
                        price={convertCurrency(finalPrice.toString(), displayCurrency)}
                        originalPrice={hasDiscount ? convertCurrency(price.toString(), displayCurrency) : undefined}
                      />
                      <Divider />
                    </Fragment>
                  );
                })
              )}
            </div>
          </div>

          <div className="shipment-subtotal">
            <Text variant="bodyMd" as="p">
              Total
            </Text>
            <Text variant="bodyMd" as="p" fontWeight="bold">
              {convertCurrency(totalPrice.toString(), displayCurrency)}
            </Text>
          </div>
        </Card>
      </StyledShipmentInfo>
      <CourierModal />
    </>
  );
};

export default ShipmentInfo;
